import React, { useState } from "react";
import {
  Card,
  CardHeader,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, MessageCircle, Calculator } from "lucide-react";
import LangGraphChat from "./LangGraphChat";
import testimonial1 from "@/images/testimonial1.webp";
import testimonial2 from "@/images/testimonial2.webp";
import { useForm, Controller, FormProvider } from "react-hook-form";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import ApiService, { ApiError } from "@/services/api";
import { EndpointKeys } from "@/config/api";

interface SalaryFormData {
  tax_year: number;
  tax_region: "UK";
  marital_status: "single" | "married" | "divorced" | "widowed";
  age: number;
  gross_salary: number;
  pay_frequency: "weekly" | "fortnightly" | "monthly" | "annually";
  pay_date: string;
  pay_period_number: number;
  num_pay_periods: number;
  tax_code: string;
  tax_regime: "S" | "C" | "";
  is_cumulative: boolean;
  pension_contribution: number;
  pension_contribution_type: "percentage" | "fixed";
  student_loan_plans: string[];
  allowances: number;
  deductions: number;
}

interface CalculationResult {
  breakdown: {
    gross_salary: string;
    tax_free_allowance: string;
    taxable_income: string;
    income_tax: {
      total: string;
      breakdown: Array<{
        band: string;
        amount: string;
        rate: string;
      }>;
    };
    national_insurance: string;
    employer_ni: string;
    pension_contribution: string;
    student_loan_repayment: string;
    total_deductions: string;
    total_taxes: string;
    net_salary: string;
  };
  percentages: {
    income_tax: number;
    national_insurance: number;
    employer_ni: number;
    pension: number;
    student_loan: number;
    total_deductions: number;
    total_taxes: number;
    net_salary: number;
  };
  period_amounts: {
    [key: string]: {
      weekly: number;
      monthly: number;
      yearly: number;
    };
  };
}

const steps = [
  "Basic Information",
  "Tax Details",
  "Deductions",
  "Review & Calculate",
];

const SalaryCalculator: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [calculationResult, setCalculationResult] =
    useState<CalculationResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showIntro, setShowIntro] = useState(true);
  const [calculatorMode, setCalculatorMode] = useState<"intro" | "form" | "chat">("intro");

  const methods = useForm<SalaryFormData>({
    defaultValues: {
      tax_year: new Date().getFullYear(),
      tax_region: "UK",
      marital_status: "single",
      age: 30,
      gross_salary: 0,
      pay_frequency: "monthly",
      pay_date: new Date().toISOString().split("T")[0],
      pay_period_number: 1,
      num_pay_periods: 12,
      tax_code: "1257L",
      tax_regime: "",
      is_cumulative: true,
      pension_contribution: 0,
      pension_contribution_type: "percentage",
      student_loan_plans: [],
      allowances: 0,
      deductions: 0,
    },
  });

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = methods;

  const handleNext = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
    }
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const onSubmit = async (data: SalaryFormData) => {
    setIsSubmitting(true);
    setError(null);

    // Ensure all required fields are present and properly formatted
    const formData = {
      tax_year: new Date().getFullYear(),
      tax_region: "UK",
      marital_status: data.marital_status,
      age: Number(data.age),
      gross_salary: Number(data.gross_salary),
      pay_frequency: data.pay_frequency,
      pay_date: data.pay_date,
      pay_period_number: Number(data.pay_period_number),
      num_pay_periods: Number(data.num_pay_periods),
      tax_code: data.tax_code,
      tax_regime: data.tax_regime,
      is_cumulative: true,
      pension_contribution: Number(data.pension_contribution),
      pension_contribution_type: data.pension_contribution_type,
      student_loan_plans: data.student_loan_plans,
      allowances: 0,
      deductions: 0,
    };

    try {
      const result = await ApiService.request<CalculationResult>(
        EndpointKeys.SALARY_CALCULATOR_CALCULATION,
        {
          method: "POST",
          body: formData,
          skipAuth: true, // No auth needed for calculator
        },
      );
      setCalculationResult(result);
    } catch (error) {
      if (error instanceof ApiError) {
        setError(error.message);
      } else {
        setError(
          error instanceof Error
            ? error.message
            : "Failed to calculate salary. Please try again.",
        );
      }
      console.error("Error calculating salary:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <div className="grid grid-cols-4 gap-4">
            <FormField
              control={control}
              name="gross_salary"
              render={({ field }) => (
                <FormItem className="col-span-4">
                  <FormLabel>Annual Gross Salary</FormLabel>
                  <FormControl>
                    <Input {...field} type="number" placeholder="£" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="age"
              render={({ field }) => (
                <FormItem className="col-span-4">
                  <FormLabel>Age</FormLabel>
                  <FormControl>
                    <Input {...field} type="number" placeholder="Age" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="marital_status"
              render={({ field }) => (
                <FormItem className="col-span-4">
                  <FormLabel>Marital Status</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select marital status" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="single">Single</SelectItem>
                      <SelectItem value="married">Married</SelectItem>
                      <SelectItem value="divorced">Divorced</SelectItem>
                      <SelectItem value="widowed">Widowed</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        );
      case 1:
        return (
          <div className="grid grid-cols-4 gap-4">
            <FormField
              control={control}
              name="tax_code"
              render={({ field }) => (
                <FormItem className="col-span-4">
                  <FormLabel>Tax Code</FormLabel>
                  <FormControl>
                    <Input {...field} type="text" placeholder="Tax Code" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="tax_regime"
              render={({ field }) => (
                <FormItem className="col-span-4">
                  <FormLabel>Tax Regime</FormLabel>
                  <FormControl>
                    <RadioGroup
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                      className="flex flex-row space-x-4"
                    >
                      <FormItem className="flex items-center space-x-2">
                        <FormControl>
                          <RadioGroupItem value="" />
                        </FormControl>
                        <FormLabel>English</FormLabel>
                      </FormItem>
                      <FormItem className="flex items-center space-x-2">
                        <FormControl>
                          <RadioGroupItem value="S" />
                        </FormControl>
                        <FormLabel>Scottish</FormLabel>
                      </FormItem>
                      <FormItem className="flex items-center space-x-2">
                        <FormControl>
                          <RadioGroupItem value="C" />
                        </FormControl>
                        <FormLabel>Welsh</FormLabel>
                      </FormItem>
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        );
      case 2:
        return (
          <div className="grid grid-cols-4 gap-4">
            <FormField
              control={control}
              name="pension_contribution"
              render={({ field }) => (
                <FormItem className="col-span-4">
                  <FormLabel>Pension Contribution</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="number"
                      placeholder="Pension Contribution"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="pension_contribution_type"
              render={({ field }) => (
                <FormItem className="col-span-4">
                  <FormLabel>Pension Contribution Type</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select pension contribution type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="percentage">Percentage</SelectItem>
                      <SelectItem value="fixed">Fixed Amount</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="student_loan_plans"
              render={({ field }) => (
                <FormItem className="col-span-4">
                  <FormLabel>Student Loan Plans</FormLabel>
                  <div className="space-y-2">
                    <FormItem className="flex items-center space-x-2">
                      <FormControl>
                        <Checkbox
                          checked={field.value?.includes("plan1")}
                          onCheckedChange={(checked) => {
                            const newValue = checked
                              ? [...(field.value || []), "plan1"]
                              : (field.value || []).filter(
                                  (v) => v !== "plan1",
                                );
                            field.onChange(newValue);
                          }}
                        />
                      </FormControl>
                      <FormLabel>Plan 1</FormLabel>
                    </FormItem>
                    <FormItem className="flex items-center space-x-2">
                      <FormControl>
                        <Checkbox
                          checked={field.value?.includes("plan2")}
                          onCheckedChange={(checked) => {
                            const newValue = checked
                              ? [...(field.value || []), "plan2"]
                              : (field.value || []).filter(
                                  (v) => v !== "plan2",
                                );
                            field.onChange(newValue);
                          }}
                        />
                      </FormControl>
                      <FormLabel>Plan 2</FormLabel>
                    </FormItem>
                    <FormItem className="flex items-center space-x-2">
                      <FormControl>
                        <Checkbox
                          checked={field.value?.includes("plan4")}
                          onCheckedChange={(checked) => {
                            const newValue = checked
                              ? [...(field.value || []), "plan4"]
                              : (field.value || []).filter(
                                  (v) => v !== "plan4",
                                );
                            field.onChange(newValue);
                          }}
                        />
                      </FormControl>
                      <FormLabel>Plan 4</FormLabel>
                    </FormItem>
                    <FormItem className="flex items-center space-x-2">
                      <FormControl>
                        <Checkbox
                          checked={field.value?.includes("plan5")}
                          onCheckedChange={(checked) => {
                            const newValue = checked
                              ? [...(field.value || []), "plan5"]
                              : (field.value || []).filter(
                                  (v) => v !== "plan5",
                                );
                            field.onChange(newValue);
                          }}
                        />
                      </FormControl>
                      <FormLabel>Plan 5</FormLabel>
                    </FormItem>
                    <FormItem className="flex items-center space-x-2">
                      <FormControl>
                        <Checkbox
                          checked={field.value?.includes("postgrad")}
                          onCheckedChange={(checked) => {
                            const newValue = checked
                              ? [...(field.value || []), "postgrad"]
                              : (field.value || []).filter(
                                  (v) => v !== "postgrad",
                                );
                            field.onChange(newValue);
                          }}
                        />
                      </FormControl>
                      <FormLabel>Postgraduate Loan</FormLabel>
                    </FormItem>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        );
      case 3:
        return (
          <div className="text-center">
            <p>
              Review your information and click Calculate to see your results.
            </p>
          </div>
        );
      default:
        return null;
    }
  };

  // Show chat mode
  if (calculatorMode === "chat") {
    return <LangGraphChat onBack={() => setCalculatorMode("intro")} />;
  }

  if (calculatorMode === "intro") {
    return (
      <div className="flex flex-col justify-center items-center min-h-screen bg-white p-4 md:p-8">
        <h1 className="text-4xl md:text-5xl text-primary mb-8">Hirelo</h1>
        <div className="relative w-full max-w-4xl">
          <img
            src={testimonial1}
            alt="Testimonial 1"
            className="absolute z-10 w-20 h-20 md:w-24 md:h-24 rounded-full object-cover shadow-lg top-3/5 left-6 md:left-8 transform -translate-y-1/2"
          />
          <img
            src={testimonial2}
            alt="Testimonial 2"
            className="absolute z-10 w-20 h-20 md:w-24 md:h-24 rounded-full object-cover shadow-lg top-1/2 right-6 md:right-8 transform -translate-y-1/2"
          />
          <Card className="w-full p-8 md:p-12 text-center bg-slate-100">
            <CardHeader>
              <h2 className="text-3xl md:text-4xl mb-8">
                Calculate your nanny's{" "}
                <span style={{ color: "#E98063", fontFamily: "inherit" }}>
                  take-home pay
                </span>
              </h2>
            </CardHeader>
            <CardContent>
              <p className="text-lg text-muted-foreground mb-10 md:mb-12">
                Get an accurate breakdown of your nanny's salary, including tax
                deductions, National Insurance, and other contributions.
              </p>
              
              {/* Calculator Mode Selection */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10 md:mb-12">
                <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer border-2 hover:border-primary/50" onClick={() => setCalculatorMode("chat")}>
                  <div className="flex flex-col items-center text-center">
                    <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                      <MessageCircle className="h-8 w-8 text-primary" />
                    </div>
                    <h3 className="text-xl font-semibold mb-2">AI Chat Calculator</h3>
                    <p className="text-muted-foreground text-sm mb-4">
                      Talk to our AI assistant for a guided, conversational experience
                    </p>
                    <div className="space-y-2 text-left text-sm">
                      <div className="flex items-center">
                        <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                        <span>Natural conversation</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                        <span>Step-by-step guidance</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                        <span>Instant results</span>
                      </div>
                    </div>
                  </div>
                </Card>

                <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer border-2 hover:border-primary/50" onClick={() => setCalculatorMode("form")}>
                  <div className="flex flex-col items-center text-center">
                    <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                      <Calculator className="h-8 w-8 text-primary" />
                    </div>
                    <h3 className="text-xl font-semibold mb-2">Form Calculator</h3>
                    <p className="text-muted-foreground text-sm mb-4">
                      Traditional form-based calculator with detailed options
                    </p>
                    <div className="space-y-2 text-left text-sm">
                      <div className="flex items-center">
                        <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                        <span>Detailed breakdown</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                        <span>Advanced options</span>
                      </div>
                      <div className="flex items-center">
                        <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                        <span>Period comparisons</span>
                      </div>
                    </div>
                  </div>
                </Card>
              </div>

              <div className="space-y-3 text-left mx-auto max-w-md text-base md:text-lg">
                <div className="flex items-center">
                  <CheckCircle className="h-6 w-6 text-green-500 mr-3" />
                  <span>Accurate tax calculations</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-6 w-6 text-green-500 mr-3" />
                  <span>Includes all deductions</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-6 w-6 text-green-500 mr-3" />
                  <span>Free and easy to use</span>
                </div>
              </div>
            </CardContent>
            <CardFooter className="text-sm text-muted-foreground justify-center mt-10 md:mt-12">
              <p>© Hirelo {new Date().getFullYear()} • All rights reserved</p>
            </CardFooter>
          </Card>
        </div>
      </div>
    );
  }

  if (isSubmitting) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Card className="w-full max-w-xl p-6">
          <div className="flex flex-col items-center justify-center space-y-4">
            <div className="animate-spin h-8 w-8 border-2 border-primary rounded-full border-t-transparent"></div>
            <p>Calculating your salary...</p>
          </div>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Card className="w-full max-w-xl p-6">
          <div className="text-center">
            <h2 className="text-2xl mb-4">Error</h2>
            <p className="mb-4 text-destructive">{error}</p>
            <Button onClick={() => setError(null)}>Try Again</Button>
          </div>
        </Card>
      </div>
    );
  }

  if (calculationResult) {
    return (
      <div className="flex justify-center items-center min-h-screen p-4">
        <Card className="w-full max-w-4xl">
          <CardHeader>
            <h2 className="text-2xl text-center">Salary Calculation Results</h2>
          </CardHeader>
          <CardContent>
            <div className="mt-4">
              <h3 className="text-xl mb-4">Summary</h3>

              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-sm text-muted-foreground mb-2">
                      Gross Salary
                    </p>
                    <p className="text-2xl font-semibold">
                      £
                      {Number(
                        calculationResult.breakdown.gross_salary,
                      ).toLocaleString()}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-sm text-muted-foreground mb-2">
                      Net Salary
                    </p>
                    <p className="text-2xl font-semibold">
                      £
                      {Number(
                        calculationResult.breakdown.net_salary,
                      ).toLocaleString()}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-sm text-muted-foreground mb-2">
                      Total Deductions
                    </p>
                    <p className="text-2xl font-semibold">
                      £
                      {Number(
                        calculationResult.breakdown.total_deductions,
                      ).toLocaleString()}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Detailed Breakdown */}
              <Card className="mb-8">
                <CardContent className="pt-6">
                  <h3 className="text-xl mb-4">Detailed Breakdown</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="text-lg mb-3">Income Tax</h4>
                      <div className="space-y-2">
                        {calculationResult.breakdown.income_tax.breakdown.map(
                          (band, index) => (
                            <div
                              key={index}
                              className="flex justify-between items-center"
                            >
                              <div>
                                <p>{band.band}</p>
                                <p className="text-sm text-muted-foreground">
                                  Rate: {band.rate}%
                                </p>
                              </div>
                              <p>£{Number(band.amount).toLocaleString()}</p>
                            </div>
                          ),
                        )}
                        <div className="flex justify-between items-center font-semibold pt-2 border-t">
                          <p>Total Income Tax</p>
                          <p>
                            £
                            {Number(
                              calculationResult.breakdown.income_tax.total,
                            ).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div>
                      <h4 className="text-lg mb-3">Other Deductions</h4>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <p>National Insurance</p>
                          <p>
                            £
                            {Number(
                              calculationResult.breakdown.national_insurance,
                            ).toLocaleString()}
                          </p>
                        </div>
                        <div className="flex justify-between items-center">
                          <p>Employer NI</p>
                          <p>
                            £
                            {Number(
                              calculationResult.breakdown.employer_ni,
                            ).toLocaleString()}
                          </p>
                        </div>
                        <div className="flex justify-between items-center">
                          <p>Pension Contribution</p>
                          <p>
                            £
                            {Number(
                              calculationResult.breakdown.pension_contribution,
                            ).toLocaleString()}
                          </p>
                        </div>
                        <div className="flex justify-between items-center">
                          <p>Student Loan</p>
                          <p>
                            £
                            {Number(
                              calculationResult.breakdown
                                .student_loan_repayment,
                            ).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Period Amounts */}
              <Card>
                <CardContent className="pt-6">
                  <h3 className="text-xl mb-4">Period Amounts</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2">Description</th>
                          <th className="text-right py-2">Weekly</th>
                          <th className="text-right py-2">Monthly</th>
                          <th className="text-right py-2">Yearly</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(calculationResult.period_amounts).map(
                          ([key, value]) => (
                            <tr key={key} className="border-b">
                              <td className="py-2">
                                {key
                                  .split("_")
                                  .map(
                                    (word) =>
                                      word.charAt(0).toUpperCase() +
                                      word.slice(1),
                                  )
                                  .join(" ")}
                              </td>
                              <td className="text-right py-2">
                                £{Number(value.weekly).toLocaleString()}
                              </td>
                              <td className="text-right py-2">
                                £{Number(value.monthly).toLocaleString()}
                              </td>
                              <td className="text-right py-2">
                                £{Number(value.yearly).toLocaleString()}
                              </td>
                            </tr>
                          ),
                        )}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </div>
          </CardContent>
          <CardFooter className="flex justify-center gap-4">
            <Button
              variant="outline"
              onClick={() => {
                setCalculationResult(null);
                setActiveStep(0);
              }}
            >
              Calculate Again
            </Button>
            <Button
              onClick={() => {
                setCalculationResult(null);
              }}
            >
              Edit Details
            </Button>
            <Button
              variant="ghost"
              onClick={() => setCalculatorMode("intro")}
            >
              Back to Start
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <FormProvider {...methods}>
      <div className="flex flex-col justify-center items-center min-h-screen p-4 md:p-8 bg-white">
        <h1 className="text-4xl md:text-5xl text-primary mb-16">Hirelo</h1>
        <Card className="w-full max-w-4xl bg-slate-100 mt-16">
          <CardContent className="pt-8 pb-6 px-6 md:px-8">
            <Button
              variant="link"
              className="mb-8 p-0 h-auto font-normal text-base"
              onClick={() => setCalculatorMode("intro")}
            >
              ← Back to start
            </Button>

            <h2 className="text-3xl text-center mb-10">Salary Calculator</h2>
            <p className="text-lg text-muted-foreground text-center mb-10">
              Calculate your take-home pay in just a few simple steps
            </p>

            <div className="flex items-center gap-3 mb-10">
              <div className="w-full h-2.5 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary"
                  style={{
                    width: `${((activeStep + 1) / steps.length) * 100}%`,
                  }}
                />
              </div>
              <span className="text-base text-muted-foreground whitespace-nowrap">
                {activeStep + 1} / {steps.length}
              </span>
            </div>

            <form onSubmit={handleSubmit(onSubmit)}>
              {renderStepContent(activeStep)}

              <div className="flex justify-between mt-10">
                <Button
                  variant="outline"
                  onClick={handleBack}
                  disabled={activeStep === 0}
                  className="text-base py-2 px-4"
                  type="button"
                >
                  Previous
                </Button>

                {activeStep === steps.length - 1 ? (
                  <Button
                    size="lg"
                    type="submit"
                    className="bg-[#E98063] text-white hover:bg-[#E98063]/90"
                  >
                    Calculate
                  </Button>
                ) : (
                  <Button
                    size="lg"
                    onClick={handleNext}
                    type="button"
                    className="bg-[#E98063] text-white hover:bg-[#E98063]/90"
                  >
                    Next
                  </Button>
                )}
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </FormProvider>
  );
};

export default SalaryCalculator;
